import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebaseConfig'; 
import logo from '../img/logo.png';
import { useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';
import { setStoredTutorType } from '../utils/roleTerminology';

// CSS styles
const CenteredContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
});

const Form = styled('form')(({ theme }) => ({
  width: '100%', 
  marginTop: theme.spacing(1),
  boxSizing: 'border-box',
}));

const PaperContainer = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  borderRadius: '5px',
  width: '90%', 
  maxWidth: '600px', 
  boxSizing: 'border-box',
}));

const LogoImage = styled('img')({
  width: '300px',
  height: 'auto',
});

const AvatarIcon = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
}));

export default function TeacherSignIn() {

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allowForgotPassword, setAllowForgotPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fullyRegistered, setFullyRegistered] = useState(null);
  const [teacherDocId, setTeacherDocId] = useState(null);
  const [detectedTutorType, setDetectedTutorType] = useState('Teacher');

    // ===== Language switcher (top-right) =====
    const LANGS = [
      { code: "ar", label: "AR" },
      { code: "fr", label: "FR" },
      { code: "en", label: "EN" },
    ];

    const handleChangeLanguage = (langCode) => {
      i18n.changeLanguage(langCode);
      // Optional persistence (recommended)
      localStorage.setItem("i18nextLng", langCode);
    };

  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === "rtl";
  const fieldDirection = isRtl ? "rtl" : "ltr";
  const authFieldSx = {
    direction: fieldDirection,
    "& .MuiInputLabel-root": {
      left: isRtl ? "auto" : 0,
      right: isRtl ? 0 : "auto",
      transformOrigin: isRtl ? "top right" : "top left",
      textAlign: isRtl ? "right" : "left",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
      textAlign: isRtl ? "right" : "left",
    },
    "& input": {
      textAlign: isRtl ? "right" : "left",
    },
  };


  React.useEffect(() => {
    // Show "Forgot Password" only when we want to LOGIN:
    if (fullyRegistered === true) {
      setAllowForgotPassword(true);
    } else {
      setAllowForgotPassword(false);
    }
  }, [fullyRegistered]);


  const handleLogin = async () => {
    setError('')
    setSuccess('')

    try {

      const normalizedEmail = email.trim().toLowerCase();

      const teachersQuery = query(
        collection(db, 'teachers'),
        where('email', '==', normalizedEmail)
      );
      const querySnapshot = await getDocs(teachersQuery);

      if (!querySnapshot.empty) {
        const teacherData = querySnapshot.docs[0].data();
        setFullyRegistered(teacherData.fullyRegistered);
        setTeacherDocId(querySnapshot.docs[0].id); 
        setDetectedTutorType(teacherData?.tutorType || 'Teacher');
      } else {
        setError(t('! البريد الإلكتروني غير موجود'));
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setError(t('حدث خطأ أثناء التحقق من البريد الإلكتروني'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('')
    setSuccess('')
    
    try {

      const normalizedEmail = email.trim().toLowerCase();
      let effectiveTutorType = detectedTutorType || "Teacher";

      try {
        const teacherSnap = await getDocs(
          query(collection(db, "teachers"), where("email", "==", normalizedEmail))
        );
        if (!teacherSnap.empty) {
          effectiveTutorType = teacherSnap.docs[0].data()?.tutorType || effectiveTutorType;
        }
      } catch (e) {
        console.error("Failed to resolve tutor type during login:", e);
      }

      if (fullyRegistered) {
        // Login
        await signInWithEmailAndPassword(auth, normalizedEmail, password);
        setStoredTutorType(effectiveTutorType);
        setSuccess(t('! تم تسجيل الدخول بنجاح'));
        navigate('/teacher/classes');
      } else {
        // Register
        if (password !== confirmPassword) {
          setError(t('! كلمات السر غير متطابقة'));
          return;
        }
  
        const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
        const userId = userCredential.user.uid;
        setStoredTutorType(effectiveTutorType);
        setSuccess(t('! تم التسجيل بنجاح'));
        navigate('/teacher/classes');
  
        // Update fullyRegistered field and authID in Firestore
        if (teacherDocId) {
          const teacherRef = doc(db, 'teachers', teacherDocId);
          await updateDoc(teacherRef, {
            fullyRegistered: true,
            uid: userId, 
          });
        }
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      if (error.code === 'auth/weak-password') {
        setError(t('كلمة السر ضعيفة جدًا. يرجى اختيار كلمة سر أقوى'));
      } else {
        setError(t('حدث خطأ أثناء العملية. يرجى المحاولة مرة أخرى'));
      }
    }
  };
  

    // Forgot password functionlaity: 
    const handleForgotPassword = async () => {
      setError('');     
      setSuccess('');   
    
      if (!email) {
        setError(t('يرجى إدخال البريد الإلكتروني لإعادة تعيين كلمة المرور.'));
        return;
      }
    
      try {
        await sendPasswordResetEmail(auth, email);
        setSuccess(t('تم إرسال رسالة استعادة كلمة المرور إلى بريدك الإلكتروني.'));
      } catch (err) {
        console.error('Error sending reset email:', err);
        setError(t('حدث خطأ أثناء إرسال رسالة استعادة كلمة المرور. يرجى المحاولة لاحقًا.'));
      }
    };


  return (
    <Box
      sx={{
        position: "relative",
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
      }}
    >

      {/* Top-right language switcher */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          display: "flex",
          gap: 1,
          zIndex: 10,
        }}
      >
        {LANGS.map((l) => {
          const active = i18n.language?.startsWith(l.code);
          return (
            <Button
              key={l.code}
              size="small"
              variant={active ? "contained" : "outlined"}
              onClick={() => handleChangeLanguage(l.code)}
              sx={{
                minWidth: 46,
                borderRadius: 999,
                fontWeight: 800,
                textTransform: "none",
                ...(active
                  ? { bgcolor: "#12344d", "&:hover": { bgcolor: "#0f2b3e" } }
                  : { borderColor: "rgba(0,0,0,0.15)", color: "#111827" }),
              }}
            >
              {l.label}
            </Button>
          );
        })}
      </Box>

      <CssBaseline />
      <CenteredContainer component="main" maxWidth="sm">
        <LogoImage src={logo} alt={t('Logo')} />
        <PaperContainer>
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 500,
                color: "#111827", 
                mb: 0.5,
              }}
            >
              {t('تسجيل دخول الأستاذ')}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#6b7280", 
                fontWeight: 400,
              }}
            >
              {t('أدخل بياناتك للوصول إلى حسابك الدراسي')}
            </Typography>
          </Box>

          
          <Form noValidate onSubmit={handleSubmit} dir={fieldDirection}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TextField
                variant="standard"
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('عنوان البريد الإلكتروني')}
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ dir: fieldDirection }}
                InputProps={{
                  readOnly: fullyRegistered !== null,
                  endAdornment: fullyRegistered !== null ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('تغيير البريد الإلكتروني')}
                        onClick={() => window.location.reload()}
                        edge="end"
                        sx={{ color: '#12344d' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={authFieldSx}
              />
            </Box>
            {fullyRegistered !== null && (
              <>
                <TextField
                  variant="standard"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('كلمة المرور')}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  inputProps={{ dir: fieldDirection }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? t('إخفاء كلمة المرور') : t('إظهار كلمة المرور')}
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={authFieldSx}
                />
                {!fullyRegistered && (
                  <TextField
                    variant="standard"
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label={t('تأكيد كلمة المرور')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    inputProps={{ dir: fieldDirection }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showConfirmPassword ? t('إخفاء كلمة المرور') : t('إظهار كلمة المرور')}
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={authFieldSx}
                  />
                )}
              </>
            )}

            {error && (
              <Typography color="error" variant="body2" align="center" sx={{fontSize: '17px', marginTop: '20px'}}>
                  {error}
              </Typography>
              )
            }

            {success && (
              <Typography color="success" variant="body2" align="center" sx={{fontSize: '17px', marginTop: '20px'}}>
                  {success}
              </Typography>
              )
            }
            
            <SubmitButton
              type="button"
              fullWidth
              variant="contained"
              color="primary"
              onClick={fullyRegistered === null ? handleLogin : handleSubmit}
              sx={{borderRadius: '20px', backgroundColor:"#12344d"}}
            >
              {fullyRegistered === null ? t('تحقق من حسابي') : t('دخول إلى حسابي')}
            </SubmitButton>

            {allowForgotPassword && (
              <Container>
                <Container>
                  <Link
                    variant="body2"
                    onClick={handleForgotPassword} 
                    style={{ cursor: 'pointer', textDecoration: 'underline' }} 
                  >
                     {t('نسيت كلمة المرور؟')}
                  </Link>
                </Container>
              </Container>
            )}
            
          </Form>
        </PaperContainer>
      </CenteredContainer>
    </Box>
  );
}
